import { alpha, Stack, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { TimelineRowButton } from "web_myplann/NotificationCenter/Components/TimelineRowButton";
import { UnreadDot } from "web_myplann/NotificationCenter/Components/UnreadDot";
import type { TimelineItemLayoutProps } from "web_myplann/NotificationCenter/Types/TimelineItemLayoutProps";
import { TimelineRowVariant } from "web_myplann/NotificationCenter/Types/TimelineRowVariant";
import { mapTimelineEventLines } from "web_myplann/NotificationCenter/Utils/mapTimelineEventLines";
import { CategoryIcon } from "web_myplann/Shared/Components/CategoryIcon";
import { EventRowContent } from "web_myplann/Shared/Components/EventRowContent";
import { useFormatUserWithType } from "web_myplann/Shared/Hooks/useFormatUserWithType";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

export const TimelineItemV2Layout: FC<TimelineItemLayoutProps> = ({
	item,
	dateLine,
	secondaryDateLine,
	unread = false,
	showChevron = false,
	disabled = false,
	onClick,
	isLast = false,
	variant = TimelineRowVariant.Timeline,
	"data-testid": dataTestId,
}) => {
	const { palette, spacingTokens, iconSize, activity, notificationCenter } =
		useTheme();
	const { t } = useResource();
	const formatUserWithType = useFormatUserWithType();
	const lines = mapTimelineEventLines(
		item,
		joinTruthy([dateLine, secondaryDateLine], " "),
		t(Resources.Activity.List.Task),
		variant,
		formatUserWithType,
	);
	const isTaskRow = variant === TimelineRowVariant.Task;
	const pressedColor = isTaskRow ? palette.grey[50] : palette.grey[100];

	return (
		<TimelineRowButton
			isLast={isLast}
			backgroundColor={
				unread
					? alpha(palette.blue[50], notificationCenter.unreadTintOpacity)
					: palette.background.paper
			}
			dividerColor={palette.surface.border}
			disabled={disabled}
			onClick={onClick}
			sx={{
				position: "relative",
				py: spacingTokens.md,
				pl: isTaskRow ? spacingTokens.lg : spacingTokens.xl,
				pr: spacingTokens.lg,
				"&:active": { transform: "none", backgroundColor: pressedColor },
			}}
			data-testid={dataTestId}
		>
			{unread && (
				<UnreadDot
					color={palette.blue[500]}
					inset={notificationCenter.unreadDot.inset}
				/>
			)}

			<Stack
				direction="row"
				gap={spacingTokens.md}
				width="100%"
				alignItems="flex-start"
			>
				<CategoryIcon item={item} />

				<EventRowContent
					{...lines}
					lineHeight={notificationCenter.rowLineHeight}
				/>

				{showChevron && (
					<Stack
						flexShrink={0}
						mt={isTaskRow ? 0 : `${activity.eventRow.chevronOffset}px`}
					>
						<ChevronRight
							size={iconSize.sm}
							color={isTaskRow ? palette.grey[400] : palette.grey[300]}
						/>
					</Stack>
				)}
			</Stack>
		</TimelineRowButton>
	);
};
